import { motion } from 'framer-motion';
import { User, Mail, Phone, ArrowLeft, ArrowRight } from 'lucide-react';
import { AnimatedInput } from '../AnimatedInput';
import { AnimatedButton } from '../AnimatedButton';
import { springConfig, authThemeConfigs, type AuthTheme } from '../constants';
import type { RegisterFormData } from './types';
import { stepConfig } from './types';

interface PersonalInfoStepProps {
  formData: RegisterFormData;
  errors: Record<string, string>;
  onChange: (field: keyof RegisterFormData, value: string) => void;
  onBack: () => void;
  onNext: () => void;
  theme?: AuthTheme;
}

export function PersonalInfoStep({
  formData,
  errors,
  onChange,
  onBack,
  onNext,
  theme = 'invol',
}: PersonalInfoStepProps) {
  const config = stepConfig.personal_info;
  const themeConfig = authThemeConfigs[theme] || authThemeConfigs.invol;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg ${
            themeConfig.iconPrimaryIsGradient
              ? `bg-gradient-to-br ${themeConfig.iconPrimary} shadow-purple-500/25`
              : `${themeConfig.iconPrimary} ${themeConfig.buttonShadow}`
          }`}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={springConfig.bouncy}
        >
          <User className={`h-6 w-6 ${themeConfig.iconColor}`} />
        </motion.div>
        <motion.h2
          className="text-2xl font-bold text-slate-900"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {config.title}
        </motion.h2>
        <motion.p
          className="text-slate-500 mt-1"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {config.subtitle}
        </motion.p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name fields - side by side */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <AnimatedInput
              id="first_name"
              label="First name"
              placeholder="John"
              value={formData.first_name}
              onChange={(e) => onChange('first_name', e.target.value)}
              error={errors.first_name}
              autoComplete="given-name"
              autoFocus
              theme={theme}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25 }}
          >
            <AnimatedInput
              id="last_name"
              label="Last name"
              placeholder="Doe"
              value={formData.last_name}
              onChange={(e) => onChange('last_name', e.target.value)}
              error={errors.last_name}
              autoComplete="family-name"
              theme={theme}
            />
          </motion.div>
        </div>

        {/* Email */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <AnimatedInput
            id="email"
            label="Email address"
            type="email"
            placeholder="john@example.com"
            icon={<Mail className="h-4 w-4" />}
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value)}
            error={errors.email}
            autoComplete="email"
            theme={theme}
          />
        </motion.div>

        {/* Phone (optional) */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.35 }}
        >
          <AnimatedInput
            id="phone_number"
            label="Phone number"
            type="tel"
            placeholder="+49 123 456 7890 (optional)"
            icon={<Phone className="h-4 w-4" />}
            value={formData.phone_number}
            onChange={(e) => onChange('phone_number', e.target.value)}
            error={errors.phone_number}
            autoComplete="tel"
            theme={theme}
          />
        </motion.div>

        {/* Navigation buttons */}
        <motion.div
          className="flex gap-3 pt-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <AnimatedButton
            type="button"
            variant="secondary"
            onClick={onBack}
            icon={<ArrowLeft className="h-4 w-4" />}
            iconPosition="left"
            theme={theme}
          >
            Back
          </AnimatedButton>
          <AnimatedButton
            type="submit"
            variant="primary"
            size="lg"
            className="flex-1"
            icon={<ArrowRight className="h-4 w-4" />}
            theme={theme}
          >
            Continue
          </AnimatedButton>
        </motion.div>
      </form>
    </motion.div>
  );
}
